/* ==========================================================================
   section-quick-actions.js
   Mobile-only draggable quick-actions bubble.
   - Drag vertically, snap to nearer edge, remember position (sessionStorage).
   - Tap toggles the panel (tap vs drag is distinguished by movement).
   - Auto-lifts above the sticky add-to-cart bar if present.
   - Search tile dispatches 'eb:search:open'; configured action links navigate natively.
   ========================================================================== */
(function () {
  if (customElements.get('eb-quick-actions')) return;

  var EDGE = 14;
  var BUBBLE = 58;
  var TOP_MIN = 70;
  var GAP_ABOVE_ATC = 12;
  var DRAG_THRESHOLD = 5;

  class EbQuickActions extends HTMLElement {
    connectedCallback() {
      this.bubble = this.querySelector('[data-eb-qa-toggle]');
      this.panel = this.querySelector('.eb-qa__panel');
      this.scrim = this.querySelector('.eb-qa__scrim');
      this.sectionId = this.dataset.sectionId || 'qa';
      this.side = this.dataset.defaultSide === 'left' ? 'left' : 'right';
      this.offset = parseInt(this.dataset.defaultOffset, 10) || 160;
      this.storeKey = 'eb-qa-pos-' + this.sectionId;

      this.restore();
      this.removeAttribute('hidden');
      this.applyPosition();

      this.bindDrag();
      this.bindActions();
      this.bindPulse();

      window.addEventListener('resize', this._onResize = this.applyPosition.bind(this));
      document.addEventListener('keydown', this._onKey = this.onKey.bind(this));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('keydown', this._onKey);
    }

    /* ---- position helpers ---- */
    atcOffset() {
      var bar = document.querySelector('.eb-sticky-atc:not([hidden])');
      if (!bar) return 0;
      var r = bar.getBoundingClientRect();
      if (r.height === 0 || r.top > window.innerHeight) return 0;
      return r.height + GAP_ABOVE_ATC;
    }

    clampTop(top) {
      var max = window.innerHeight - BUBBLE - EDGE - this.atcOffset();
      var safe = parseInt(getComputedStyle(document.documentElement)
        .getPropertyValue('--eb-qa-safe-bottom'), 10) || 0;
      max -= safe;
      return Math.max(TOP_MIN, Math.min(max, top));
    }

    applyPosition() {
      this.style.top = this.clampTop(this.topPx()) + 'px';
      this.style.bottom = 'auto';
      if (this.side === 'left') {
        this.style.left = EDGE + 'px';
        this.style.right = 'auto';
        this.classList.add('eb-qa--left');
        this.classList.remove('eb-qa--right');
      } else {
        this.style.right = EDGE + 'px';
        this.style.left = 'auto';
        this.classList.add('eb-qa--right');
        this.classList.remove('eb-qa--left');
      }
    }

    topPx() {
      if (this._top != null) return this._top;
      this._top = window.innerHeight - BUBBLE - EDGE - this.offset;
      return this._top;
    }

    setTop(v) { this._top = v; }

    /* ---- persistence ---- */
    restore() {
      try {
        var raw = sessionStorage.getItem(this.storeKey);
        if (!raw) return;
        var p = JSON.parse(raw);
        if (p.side) this.side = p.side;
        if (typeof p.top === 'number') this._top = p.top;
      } catch (e) {}
    }

    persist() {
      try {
        sessionStorage.setItem(this.storeKey, JSON.stringify({ side: this.side, top: this._top }));
      } catch (e) {}
    }

    /* ---- drag ---- */
    bindDrag() {
      var self = this;
      var drag = null;

      this.bubble.addEventListener('pointerdown', function (e) {
        drag = { sx: e.clientX, sy: e.clientY, oy: parseFloat(self.style.top) || self.topPx(), moved: false };
        self.bubble.setPointerCapture(e.pointerId);
        self.classList.add('is-dragging');
        self.classList.remove('is-pulsing');
      });

      this.bubble.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.sx;
        var dy = e.clientY - drag.sy;
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) drag.moved = true;
        self.style.top = self.clampTop(drag.oy + dy) + 'px';
        var side = e.clientX < window.innerWidth / 2 ? 'left' : 'right';
        if (side === 'left') { self.style.left = EDGE + 'px'; self.style.right = 'auto'; }
        else { self.style.right = EDGE + 'px'; self.style.left = 'auto'; }
      });

      this.bubble.addEventListener('pointerup', function (e) {
        self.classList.remove('is-dragging');
        if (!drag) return;
        var side = e.clientX < window.innerWidth / 2 ? 'left' : 'right';
        self.side = side;
        self.setTop(self.clampTop(parseFloat(self.style.top)));
        self.applyPosition();
        self.persist();
        if (!drag.moved) self.toggle();
        drag = null;
      });

      this.bubble.addEventListener('pointercancel', function () {
        self.classList.remove('is-dragging');
        drag = null;
      });
    }

    /* ---- open / close ---- */
    toggle() { this.classList.contains('is-open') ? this.close() : this.open(); }

    open() {
      if (this.scrim) this.scrim.hidden = false;
      this.classList.add('is-open');
      this.bubble.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');
    }

    close() {
      this.classList.remove('is-open');
      this.bubble.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      var self = this;
      if (this.scrim) {
        window.setTimeout(function () {
          if (!self.classList.contains('is-open') && self.scrim) self.scrim.hidden = true;
        }, 220);
      }
    }

    onKey(e) {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    }

    /* ---- actions ---- */
    bindActions() {
      var self = this;
      this.querySelectorAll('[data-eb-qa-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.close(); });
      });

      this.querySelectorAll('[data-eb-qa-action]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          var act = el.getAttribute('data-eb-qa-action');

          if (act === 'search') {
            e.preventDefault();
            self.close();
            document.dispatchEvent(new CustomEvent('eb:search:open'));
            return;
          }

          // Configured links navigate natively.
          self.close();
        });
      });
    }

    /* ---- one-time pulse on first scroll ---- */
    bindPulse() {
      if (this.dataset.pulse === 'false') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var self = this;
      var fired = false;
      var onScroll = function () {
        if (fired) return;
        fired = true;
        self.classList.add('is-pulsing');
        window.setTimeout(function () { self.classList.remove('is-pulsing'); }, 1500);
        window.removeEventListener('scroll', onScroll);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  customElements.define('eb-quick-actions', EbQuickActions);
})();
